import { optionFields, type ToolOptions } from "./options.js";
import { readSettings } from "./control.js";
import { defaultOptions } from "./options.js";
import { readerSignedIn } from "./reader-session.js";
import { valueAttribute } from "./control.js";

/** Where the handler answers about the tools' settings. */
const accountPath = "/user/tool-settings/";

/**
 * The tools' settings as the account holds them.
 *
 * A copy that outlives the device, for a reader who is signed in.
 * `localStorage` is the device's own and is what the panel opens on; this is
 * what fills a device that has none, which is a new phone, a new browser or a
 * cleared one.
 *
 * **Only a signed-in reader is asked about.** `head/session-state.html` puts
 * `data-cb-session` on `<html>` from a cookie before the page is painted, so
 * the question is answered without a request for the great majority of readers,
 * who have no account. A hint can be stale, and a stale one costs one request
 * that answers with nothing.
 *
 * **Nothing here fails loudly.** A reader whose network drops, whose session
 * lapsed between the page loading and a control moving, or who is behind
 * something that eats the request, still has a working converter with its
 * settings in `localStorage`. The account copy is the part that can be missed.
 */
export class AccountSettings {
  readonly #page: Document;

  constructor(page: Document) {
    this.#page = page;
  }

  /** Whether this page was drawn for somebody with a session. */
  get signedIn(): boolean {
    return readerSignedIn(this.#page);
  }

  /**
   * What the account holds, or nothing.
   *
   * Nothing covers every way of not having an answer: no session, no account
   * copy, a request that failed, and a body that is not what this release
   * expects. All four leave the panel on what the device gave it, which is the
   * behaviour a reader had before they signed in.
   */
  async stored(): Promise<ToolOptions | undefined> {
    if (!this.signedIn) {
      return undefined;
    }

    try {
      const answered = await fetch(accountPath, {
        headers: { accept: "application/json" },
      });

      if (!answered.ok) {
        return undefined;
      }

      const body: unknown = await answered.json();
      const held = (body as { settings?: unknown } | null)?.settings;

      return held === undefined || held === null
        ? undefined
        : readSettings(optionFields, defaultOptions, held);
    } catch {
      return undefined;
    }
  }

  /**
   * Put what a reader has just chosen on their account.
   *
   * Form-encoded rather than JSON, which is what the handler's other route
   * takes and what `URLSearchParams` writes without a second thought about
   * content types. The booleans go as the panel's own `value` attributes write
   * them, so both sides spell a setting the same way.
   *
   * The answer is dropped. The setting is already in `localStorage` and already
   * applied to the conversion, and a reader who moved a control has had what
   * they asked for whether or not the account heard about it.
   */
  async save(options: ToolOptions): Promise<void> {
    if (!this.signedIn) {
      return;
    }

    const body = new URLSearchParams();

    for (const offered of optionFields) {
      const value = options[offered.field];
      body.set(offered.field, valueAttribute(value));
    }

    try {
      await fetch(accountPath, {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // A reader with a working converter and an account that did not hear
      // about it is in a better state than one shown an error they cannot act
      // on. The next change tries again.
    }
  }
}
